import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { Product, CartItem, CartTotals } from "@cieda/types";
import { calculateTVA } from "@cieda/utils";

// ── Constants ────────────────────────────────────────────────────────────────
const CART_EXPIRY_MS = 7 * 24 * 60 * 60 * 1000; // 7 days
const MAX_ITEM_QUANTITY = 999;

// ── Persisted shape ──────────────────────────────────────────────────────────
// We persist a product snapshot alongside quantity so the cart survives page
// reloads without needing an API call.  The snapshot contains only the fields
// needed for display & calculation — not the full product object.

interface ProductSnapshot {
  readonly id: string;
  readonly name: string;
  readonly brand: string;
  readonly unit: string;
  readonly price_fcfa: number;
  readonly image_url: string | null;
  readonly min_order_qty: number;
  readonly stock: number;
  readonly weight_kg: number;
  readonly tva_applicable: boolean;
  readonly tva_rate: number;
}

interface PersistedCartItem {
  readonly snapshot: ProductSnapshot;
  readonly quantity: number;
}

// ── Sanitisation helpers ─────────────────────────────────────────────────────

/** Coerce a value to a finite number, falling back to `fallback`. */
function safeNum(value: unknown, fallback: number): number {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

/** Build a clean snapshot with no NaN values. */
function snapshotFrom(product: Product): ProductSnapshot {
  return {
    id: product.id,
    name: product.name,
    brand: product.brand ?? "",
    unit: product.unit ?? "",
    price_fcfa: safeNum(product.price_fcfa, 0),
    image_url: product.image_url ?? null,
    min_order_qty: safeNum(product.min_order_qty, 1),
    stock: safeNum(product.stock, 0),
    weight_kg: safeNum(product.weight_kg, 0),
    tva_applicable: product.tva_applicable ?? false,
    tva_rate: safeNum(product.tva_rate, 0),
  };
}

/** Sanitise a single persisted item — returns null if unrecoverable. */
function sanitiseItem(item: PersistedCartItem): PersistedCartItem | null {
  if (!item?.snapshot?.id) return null;

  const minQty = safeNum(item.snapshot.min_order_qty, 1);
  const quantity = safeNum(item.quantity, minQty);

  return {
    snapshot: {
      ...item.snapshot,
      price_fcfa: safeNum(item.snapshot.price_fcfa, 0),
      min_order_qty: minQty,
      stock: safeNum(item.snapshot.stock, 0),
      weight_kg: safeNum(item.snapshot.weight_kg, 0),
      tva_rate: safeNum(item.snapshot.tva_rate, 0),
      tva_applicable: item.snapshot.tva_applicable ?? false,
    },
    quantity: Math.max(quantity, minQty),
  };
}

/** Sanitise the full persisted items array — drops corrupted entries. */
function sanitiseAll(items: PersistedCartItem[]): PersistedCartItem[] {
  if (!Array.isArray(items)) return [];
  const result: PersistedCartItem[] = [];
  for (const item of items) {
    const clean = sanitiseItem(item);
    if (clean) result.push(clean);
  }
  return result;
}

/** Convert persisted items → CartItem[] for consumption by UI. */
function toCartItems(persisted: PersistedCartItem[]): CartItem[] {
  return persisted.map((p) => ({
    product: p.snapshot as unknown as Product,
    quantity: p.quantity,
  }));
}

// ── Live product cache ───────────────────────────────────────────────────────
// When the user adds a product, we cache the full object so that subsequent
// reads within the same session use fresh data.
const productCache = new Map<string, Product>();

// ── Store interface ──────────────────────────────────────────────────────────

interface CartState {
  _persistedItems: PersistedCartItem[];
  _lastUpdatedAt: number;
  items: CartItem[];
  addItem: (product: Product, quantity: number) => void;
  removeItem: (productId: string) => void;
  updateQuantity: (productId: string, quantity: number) => void;
  clearCart: () => void;
  getTotals: () => CartTotals;
  /** Replace all items with fresh data (used by sync/staleness hooks). */
  _replaceItems: (items: PersistedCartItem[]) => void;
  /** Read-only access to persisted items (for sync hooks). */
  _getPersistedItems: () => readonly PersistedCartItem[];
}

export const useCartStore = create<CartState>()(
  persist(
    (set, get) => ({
      _persistedItems: [],
      _lastUpdatedAt: Date.now(),
      items: [],

      addItem: (product, quantity) => {
        productCache.set(product.id, product);
        const snapshot = snapshotFrom(product);
        const effectiveQty = Math.max(
          safeNum(quantity, 1),
          snapshot.min_order_qty,
        );

        set((state) => {
          const existing = state._persistedItems.find(
            (i) => i.snapshot.id === product.id,
          );
          const updated: PersistedCartItem[] = existing
            ? state._persistedItems.map((i) =>
                i.snapshot.id === product.id
                  ? { snapshot, quantity: Math.min(i.quantity + effectiveQty, MAX_ITEM_QUANTITY) }
                  : i,
              )
            : [
                ...state._persistedItems,
                { snapshot, quantity: Math.min(effectiveQty, MAX_ITEM_QUANTITY) },
              ];
          return {
            _persistedItems: updated,
            _lastUpdatedAt: Date.now(),
            items: toCartItems(updated),
          };
        });
      },

      removeItem: (productId) => {
        set((state) => {
          const updated = state._persistedItems.filter(
            (i) => i.snapshot.id !== productId,
          );
          return {
            _persistedItems: updated,
            _lastUpdatedAt: Date.now(),
            items: toCartItems(updated),
          };
        });
      },

      updateQuantity: (productId, quantity) => {
        set((state) => {
          const updated = state._persistedItems.map((i) => {
            if (i.snapshot.id !== productId) return i;
            const minQty = safeNum(i.snapshot.min_order_qty, 1);
            return {
              ...i,
              quantity: Math.min(Math.max(safeNum(quantity, minQty), minQty), MAX_ITEM_QUANTITY),
            };
          });
          return {
            _persistedItems: updated,
            _lastUpdatedAt: Date.now(),
            items: toCartItems(updated),
          };
        });
      },

      clearCart: () =>
        set({ _persistedItems: [], _lastUpdatedAt: Date.now(), items: [] }),

      getTotals: () => {
        const items = get().items;
        let subtotal_ht = 0;
        let tva_total = 0;
        let total_weight = 0;
        let items_count = 0;

        for (const item of items) {
          const price = safeNum(item.product.price_fcfa, 0);
          const qty = safeNum(item.quantity, 0);
          const weight = safeNum(item.product.weight_kg, 0);

          const lineHT = price * qty;
          const lineTVA = item.product.tva_applicable
            ? calculateTVA(lineHT, safeNum(item.product.tva_rate, 0))
            : 0;

          subtotal_ht += lineHT;
          tva_total += lineTVA;
          total_weight += weight * qty;
          items_count += qty;
        }

        return {
          subtotal_ht_fcfa: subtotal_ht,
          tva_amount_fcfa: tva_total,
          delivery_fee_fcfa: 0,
          total_ttc_fcfa: subtotal_ht + tva_total,
          items_count,
          total_weight_kg: total_weight,
        };
      },

      _replaceItems: (items) => {
        const sanitised = sanitiseAll(items);
        set({
          _persistedItems: sanitised,
          _lastUpdatedAt: Date.now(),
          items: toCartItems(sanitised),
        });
      },

      _getPersistedItems: () => get()._persistedItems,
    }),
    {
      name: "djayma-cart",
      partialize: (state) => ({
        _persistedItems: state._persistedItems,
        _lastUpdatedAt: state._lastUpdatedAt,
      }),
      // Rehydrate: sanitise + check expiry
      onRehydrateStorage: () => (state) => {
        if (!state) return;

        // Check expiry — clear if older than 7 days
        const lastUpdated = safeNum(state._lastUpdatedAt, 0);
        if (lastUpdated > 0 && Date.now() - lastUpdated > CART_EXPIRY_MS) {
          state._persistedItems = [];
          state._lastUpdatedAt = Date.now();
          state.items = [];
          return;
        }

        // Sanitise all items — drops corrupted entries, fixes NaN values
        const sanitised = sanitiseAll(state._persistedItems);
        state._persistedItems = sanitised;
        state.items = toCartItems(sanitised);

        // Set timestamp if missing (migration from v1)
        if (!lastUpdated) {
          state._lastUpdatedAt = Date.now();
        }
      },
      // Migrate from older formats
      migrate: (persisted: unknown, version: number) => {
        const data = persisted as Record<string, unknown>;

        // v0 → v1: old _leanItems format — can't reconstruct, clear
        if (version === 0 && data._leanItems && !data._persistedItems) {
          return { _persistedItems: [], _lastUpdatedAt: Date.now(), items: [] };
        }

        // v1 → v2: add _lastUpdatedAt if missing
        if (version === 1 && !data._lastUpdatedAt) {
          return { ...data, _lastUpdatedAt: Date.now() };
        }

        return data;
      },
      version: 2,
    },
  ),
);
